import posthog from 'posthog-js';

// Initialize PostHog
export const initializeAnalytics = () => {
  // Get environment variables with fallbacks
  const nodeEnv = import.meta.env.MODE || 'development';
  const posthogKey = import.meta.env.VITE_POSTHOG_KEY || '';
  const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';
  const enableAnalytics = import.meta.env.VITE_ENABLE_ANALYTICS === 'true';
  
  // Only initialize in production or when explicitly enabled
  if (nodeEnv === 'production' || enableAnalytics) {
    if (!posthogKey) {
      console.warn('PostHog key not found. Analytics will not be initialized.');
      return;
    }
    
    posthog.init(posthogKey, {
      api_host: posthogHost,
      // Privacy-friendly defaults
      capture_pageview: true,
      capture_pageleave: true,
      session_recording: {
        recordCrossOriginIframes: false,
      },
    });
  }
};

// Helper to check if analytics is enabled
const isAnalyticsEnabled = (): boolean => {
  const nodeEnv = import.meta.env.MODE || 'development';
  const enableAnalytics = import.meta.env.VITE_ENABLE_ANALYTICS === 'true';
  return nodeEnv === 'production' || enableAnalytics;
};

// Helper to check if debug logging should be enabled
const shouldLogAnalytics = (): boolean => {
  const nodeEnv = import.meta.env.MODE || 'development';
  const debugAnalytics = import.meta.env.VITE_DEBUG_ANALYTICS;
  
  // If VITE_DEBUG_ANALYTICS is explicitly set, use that value
  if (debugAnalytics !== undefined) {
    return debugAnalytics === 'true';
  }
  
  // Otherwise, default to true in development, false in production
  return nodeEnv === 'development';
};

// Core user journey events
export const analytics = {
  // Homepage engagement
  trackHomepageViewed: () => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track homepage_viewed');
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking homepage_viewed');
    posthog.capture('homepage_viewed', {
      timestamp: new Date().toISOString(),
    });
  },

  trackBookCardClicked: (bookId: string, bookTitle: string, position?: number) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track book_card_clicked:', { bookId, bookTitle, position });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking book_card_clicked:', { bookId, bookTitle, position });
    posthog.capture('book_card_clicked', {
      book_id: bookId,
      book_title: bookTitle,
      position_in_list: position,
      timestamp: new Date().toISOString(),
    });
  },

  trackBookPageViewed: (bookId: string, bookTitle: string, entrySource: 'homepage' | 'direct_link') => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track book_page_viewed:', { bookId, bookTitle, entrySource });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking book_page_viewed:', { bookId, bookTitle, entrySource });
    posthog.capture('book_page_viewed', {
      book_id: bookId,
      book_title: bookTitle,
      entry_source: entrySource,
      timestamp: new Date().toISOString(),
    });
  },

  trackBookListenStarted: (bookId: string, bookTitle: string, source: 'book_page' | 'homepage') => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track book_listen_started:', { bookId, bookTitle, source });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking book_listen_started:', { bookId, bookTitle, source });
    posthog.capture('book_listen_started', {
      book_id: bookId,
      book_title: bookTitle,
      source: source,
      timestamp: new Date().toISOString(),
    });
  },

  trackTrailerPlayed: (bookId: string, bookTitle: string, source: 'homepage' | 'book_page') => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track trailer_played:', { bookId, bookTitle, source });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking trailer_played:', { bookId, bookTitle, source });
    posthog.capture('trailer_played', {
      book_id: bookId,
      book_title: bookTitle,
      source: source,
      timestamp: new Date().toISOString(),
    });
  },

  trackDescriptionExpanded: (bookId: string, expanded: boolean, descriptionLength: number) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track book_description_expanded:', { bookId, expanded, descriptionLength });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking book_description_expanded:', { bookId, expanded, descriptionLength });
    posthog.capture('book_description_expanded', {
      book_id: bookId,
      action: expanded ? 'expand' : 'collapse',
      description_length: descriptionLength,
      timestamp: new Date().toISOString(),
    });
  },

  // Book reading flow
  trackBookReaderEntered: (bookId: string, bookTitle: string, entrySource: 'homepage' | 'direct_link') => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track book_reader_entered:', { bookId, bookTitle, entrySource });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking book_reader_entered:', { bookId, bookTitle, entrySource });
    posthog.capture('book_reader_entered', {
      book_id: bookId,
      book_title: bookTitle,
      entry_source: entrySource,
      timestamp: new Date().toISOString(),
    });
  },

  trackChapterStarted: (bookId: string, chapterFile: string, chapterTitle: string, isFirstChapter: boolean) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track chapter_started:', { bookId, chapterFile, chapterTitle, isFirstChapter });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking chapter_started:', { bookId, chapterFile, chapterTitle, isFirstChapter });
    posthog.capture('chapter_started', {
      book_id: bookId,
      chapter_file: chapterFile,
      chapter_title: chapterTitle,
      is_first_chapter: isFirstChapter,
      timestamp: new Date().toISOString(),
    });
  },

  // Audio engagement (critical for audio-first app)
  trackTrailerPlayStarted: (bookId: string, bookTitle: string, source: 'homepage' | 'book_page') => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track trailer_play_started:', { bookId, bookTitle, source });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking trailer_play_started:', { bookId, bookTitle, source });
    posthog.capture('trailer_play_started', {
      book_id: bookId,
      book_title: bookTitle,
      source: source,
      timestamp: new Date().toISOString(),
    });
  },

  trackTrailerCompleted: (bookId: string, durationListened: number, totalDuration: number) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track trailer_play_completed:', { bookId, durationListened, totalDuration });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking trailer_play_completed:', { bookId, durationListened, totalDuration });
    posthog.capture('trailer_play_completed', {
      book_id: bookId,
      duration_listened_seconds: Math.round(durationListened),
      total_duration_seconds: Math.round(totalDuration),
      completion_rate: totalDuration > 0 ? durationListened / totalDuration : 0,
      timestamp: new Date().toISOString(),
    });
  },

  trackTrailerStoppedEarly: (bookId: string, stopTime: number, totalDuration: number) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track trailer_stopped_early:', { bookId, stopTime });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking trailer_stopped_early:', { bookId, stopTime });
    posthog.capture('trailer_stopped_early', {
      book_id: bookId,
      stop_time_seconds: Math.round(stopTime),
      total_duration_seconds: Math.round(totalDuration),
      completion_rate: totalDuration > 0 ? stopTime / totalDuration : 0,
      timestamp: new Date().toISOString(),
    });
  },

  trackAudioPlayStarted: (bookId: string, chapterFile: string, autoPlay: boolean) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track audio_play_started:', { bookId, chapterFile, autoPlay });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking audio_play_started:', { bookId, chapterFile, autoPlay });
    posthog.capture('audio_play_started', {
      book_id: bookId,
      chapter_file: chapterFile,
      auto_play: autoPlay,
      timestamp: new Date().toISOString(),
    });
  },

  trackAudioCompleted: (bookId: string, chapterFile: string, listeningDuration: number) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track audio_completed:', { bookId, chapterFile, listeningDuration });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking audio_completed:', { bookId, chapterFile, listeningDuration });
    posthog.capture('audio_completed', {
      book_id: bookId,
      chapter_file: chapterFile,
      listening_duration_seconds: Math.round(listeningDuration),
      timestamp: new Date().toISOString(),
    });
  },

  trackAudioLoadingFailed: (bookId: string, chapterFile: string, errorMessage: string) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track audio_loading_failed:', { bookId, chapterFile, errorMessage });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking audio_loading_failed:', { bookId, chapterFile, errorMessage });
    posthog.capture('audio_loading_failed', {
      book_id: bookId,
      chapter_file: chapterFile,
      error_message: errorMessage,
      timestamp: new Date().toISOString(),
    });
  },

  // Interactive story choices
  trackStoryChoicePresented: (bookId: string, chapterFile: string, choices: string[]) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track story_choice_presented:', { bookId, chapterFile, choices });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking story_choice_presented:', { bookId, chapterFile, choices });
    posthog.capture('story_choice_presented', {
      book_id: bookId,
      chapter_file: chapterFile,
      choices_count: choices.length,
      choice_options: choices,
      timestamp: new Date().toISOString(),
    });
  },

  trackStoryPathSelected: (bookId: string, fromChapter: string, toChapter: string, choiceText: string) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track story_path_selected:', { bookId, fromChapter, toChapter, choiceText });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking story_path_selected:', { bookId, fromChapter, toChapter, choiceText });
    posthog.capture('story_path_selected', {
      book_id: bookId,
      from_chapter: fromChapter,
      to_chapter: toChapter,
      choice_text: choiceText,
      timestamp: new Date().toISOString(),
    });
  },

  trackChapterBackNavigation: (bookId: string, fromChapter: string, toChapter: string) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track chapter_back_navigation:', { bookId, fromChapter, toChapter });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking chapter_back_navigation:', { bookId, fromChapter, toChapter });
    posthog.capture('chapter_back_navigation', {
      book_id: bookId,
      from_chapter: fromChapter,
      to_chapter: toChapter,
      timestamp: new Date().toISOString(),
    });
  },

  // Preview & feedback system
  trackPreviewEndpointReached: (bookId: string, pathName: string, chaptersCompleted: number) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track preview_endpoint_reached:', { bookId, pathName, chaptersCompleted });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking preview_endpoint_reached:', { bookId, pathName, chaptersCompleted });
    posthog.capture('preview_endpoint_reached', {
      book_id: bookId,
      path_name: pathName,
      chapters_completed: chaptersCompleted,
      timestamp: new Date().toISOString(),
    });
  },

  trackFeedbackSubmitted: (bookId: string, pathCompleted: string, rating?: number, feedbackLength?: number) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track feedback_submitted:', { bookId, pathCompleted, rating, feedbackLength });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking feedback_submitted:', { bookId, pathCompleted, rating, feedbackLength });
    posthog.capture('feedback_submitted', {
      book_id: bookId,
      path_completed: pathCompleted,
      rating: rating,
      feedback_length: feedbackLength,
      timestamp: new Date().toISOString(),
    });
  },

  // Error tracking
  trackError: (errorMessage: string, component?: string, page?: string) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would track error_occurred:', { errorMessage, component, page });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Tracking error_occurred:', { errorMessage, component, page });
    posthog.capture('error_occurred', {
      error_message: errorMessage,
      component: component,
      page: page,
      timestamp: new Date().toISOString(),
    });
  },

  // User properties
  setUserProperties: (properties: Record<string, any>) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would set user properties:', properties);
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Setting user properties:', properties);
    posthog.people.set(properties);
  },

  // Identify user (optional - for when you have user accounts)
  identifyUser: (userId: string, traits?: Record<string, any>) => {
    if (!isAnalyticsEnabled()) {
      if (shouldLogAnalytics()) console.log('[Analytics] Disabled - would identify user:', { userId, traits });
      return;
    }
    if (shouldLogAnalytics()) console.log('[Analytics] Identifying user:', { userId, traits });
    posthog.identify(userId, traits);
  },
};

export default analytics;